import { useState, useRef, useEffect, useCallback } from 'react'
import { fullName } from '../lib/utils'

interface User {
  id: string
  firstName: string
  lastName: string
}

interface Props {
  value: string
  onChange: (raw: string) => void
  placeholder?: string
  rows?: number
  users: User[]
  className?: string
}

/**
 * Textarea that intercepts @-trigger and shows a user picker.
 * Internally stores @{uuid} tokens; displays them as @FirstName LastName.
 * The `value` / `onChange` operate on the raw string with @{uuid} tokens.
 */
export function MentionTextarea({ value, onChange, placeholder, rows = 3, users, className = '' }: Props) {
  const [query, setQuery] = useState('')
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Render display: replace @{uuid} with @Name
  const toDisplay = useCallback((raw: string) => {
    return raw.replace(/@\{([0-9a-f-]{36})\}/gi, (_, uid) => {
      const u = users.find(u => u.id === uid)
      return u ? `@${fullName(u.firstName, u.lastName)}` : '@unknown'
    })
  }, [users])

  // The textarea shows the display version; we maintain raw internally
  // But for simplicity, we keep the textarea value as raw and show @{uuid} tokens
  // The display textarea needs to show @Name — simplest approach: use display value
  // and convert back on change

  const filtered = query
    ? users.filter(u =>
        fullName(u.firstName, u.lastName).toLowerCase().includes(query.toLowerCase()),
      )
    : users.slice(0, 8)

  // Convert display text (e.g. "@Joni Test hello") back to raw "@{uuid} hello".
  // mentionStart positions are tracked in display-text coordinates to keep
  // them consistent with textarea.selectionStart.
  const displayToRaw = useCallback((display: string) => {
    let raw = display
    for (const u of users) {
      raw = raw.split(`@${fullName(u.firstName, u.lastName)}`).join(`@{${u.id}}`)
    }
    return raw
  }, [users])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value  // display-format text from textarea
    const cursor = e.target.selectionStart ?? text.length

    // Detect @ trigger: find last @ before cursor with no space after it
    const before = text.slice(0, cursor)
    const atIdx = before.lastIndexOf('@')

    if (atIdx !== -1) {
      const fragment = before.slice(atIdx + 1)
      if (!fragment.includes(' ') && !fragment.includes('\n')) {
        setMentionStart(atIdx)   // stored in display-text coords
        setQuery(fragment)
        setDropdownOpen(true)
        onChange(displayToRaw(text))
        return
      }
    }

    setDropdownOpen(false)
    setMentionStart(null)
    setQuery('')
    onChange(displayToRaw(text))
  }

  const insertMention = (user: User) => {
    if (mentionStart === null) return
    const textarea = textareaRef.current!
    const cursor = textarea.selectionStart ?? displayValue.length
    // Work entirely in display-text space, then convert to raw at the end.
    // mentionStart is already in display-text coords (set by handleChange).
    const before = displayValue.slice(0, mentionStart)
    const after = displayValue.slice(cursor)
    const newDisplay = `${before}@${fullName(user.firstName, user.lastName)} ${after}`
    const newRaw = displayToRaw(newDisplay)
    onChange(newRaw)
    setDropdownOpen(false)
    setMentionStart(null)
    setQuery('')
    // Restore focus and cursor position after the inserted mention
    setTimeout(() => {
      textarea.focus()
      const pos = before.length + `@${fullName(user.firstName, user.lastName)} `.length
      textarea.setSelectionRange(pos, pos)
    }, 0)
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current && !textareaRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const displayValue = toDisplay(value)

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        className={`input resize-none ${className}`}
      />
      {dropdownOpen && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 bottom-full left-0 mb-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <p className="px-3 py-1.5 text-xs text-gray-400 font-medium border-b border-gray-100">Mention a user</p>
          {filtered.map(u => (
            <button
              key={u.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); insertMention(u) }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs flex items-center justify-center font-medium">
                {u.firstName[0]}{u.lastName[0]}
              </span>
              {fullName(u.firstName, u.lastName)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
